import { defineChain, type Address } from "viem"
import type { BootstrapResponse } from "../api/schemas"
import { responseNow } from "../api/response-time"
import { merkleV4ConfigFromBootstrap, validateMerkleV4Bootstrap } from "./merkle-v4"

/** V4 deployment bindings updated from the supplied token/pool addresses.
 * NFT implementation evidence: merkle-v4-live-v2.json, block 289197.
 * Trade resolves its configuration from bootstrap. Policy V2 is unchanged. */
export const ELYSIUM = Object.freeze({
  chainId: 99901,
  deploymentBlock: 273425n,
  token: "0x1FFcA184d33e4Af4d8727BF7678A8b1A07B6b2bb" as Address,
  poolManager: "0xE2e105d3F7209A9DA11f83cCf3E7398a753823F1" as Address,
  poolId: "0x8c76afc5a45caad0dbabbf9fce5a9f049f67bf8f045c6831c8bb1636dd447d18" as const,
  hook: "0xBA0BbE8F60eFdF83A0578f0E55496D32606480cc" as Address,
  lpLock: "0x48944B4d9e8be4D41418EfB7bE9A7476E75372dE" as Address,
  vault: "0x1c55E07cEc24Fae3126cdfA4947675BecEB223b8" as Address,
  collection: "0xEAb107fD700a1f392b887FECB248F101AD6eEa20" as Address,
  metadata: "0x2FC2F4CdCB70d649b589A4c6dF8661399e1C9020" as Address,
  instanceId:
    "0x26c628d65e2d6d683db37dac8a43a67ddf45d760aa2353a528ca71a8fc299752" as const,
  implementation: "0x903cc7d3627b874663fb1808eb4d09d047076c1e" as Address,
  implementationCodeHash:
    "0xfb940e9ccef6c06f9ce6b93475e607a59cf638b44dcdafa113cde5747b0fe079" as const,
  signer: "0xD75372133aB4C3E5f6D0A537303B32709731E08B" as Address,
  readiness: "0xb3d2C482950cC15B9d38F51b06e7bFd09A03Bf93" as Address,
  policyVersion: "2",
  policyHash:
    "0xcd3f4b5e3bae02e681611bffbdf89a2167ea98753387ed613bfa3a0b48105993" as const,
  freeMintPolicyVersion: "1",
  freeMintPolicyHash:
    "0x81953acd01baf41b047a73b474ed6a55a6615ea7e2d1af0102fbdfd2f8544c80" as const,
  socialRewardPolicyHash:
    "0xb243c021cb36a462aafd62785c09db1f1a74205e628c42e8d7771572a0499065" as const,
  taskMintCap: 1666,
})
export const elysium = defineChain({
  id: ELYSIUM.chainId,
  name: "Elysium",
  nativeCurrency: { name: "HYPE", symbol: "HYPE", decimals: 18 },
  // No inferred public RPC. Ops must supply an approved endpoint.
  rpcUrls: { default: { http: [] } },
})
export const sameIdentity = (a: string | null | undefined, b: string) =>
  a?.toLowerCase() === b.toLowerCase()

/** Reward version 1 is distinct from the EIP-712 domain version 3. */
export function isElysiumFreePolicy(
  version: string | bigint,
  hash: string | null | undefined,
): boolean {
  return (
    String(version) === "1" &&
    (sameIdentity(hash, ELYSIUM.freeMintPolicyHash) ||
      sameIdentity(hash, ELYSIUM.socialRewardPolicyHash))
  )
}
export function isElysiumNftPolicy(
  version: string | bigint,
  hash: string | null | undefined,
): boolean {
  return (
    isElysiumFreePolicy(version, hash) ||
    (String(version) === "2" && sameIdentity(hash, ELYSIUM.policyHash))
  )
}

/** Connected migration defaults to Elysium. Retained Robinhood needs an explicit legacy context. */
export function validateConnectedDeployment(
  bootstrap: BootstrapResponse,
): void {
  const target =
    import.meta.env.VITE_CONNECTED_DEPLOYMENT?.trim() || "ELYSIUM_V3"
  if (target === "ROBINHOOD_LEGACY") {
    if (
      bootstrap.sourceChainId !== 4663 ||
      bootstrap.deployment.sourceChainId !== 4663
    )
      throw new Error("LEGACY_DEPLOYMENT_IDENTITY_MISMATCH")
    return
  }
  if (target !== "ELYSIUM_V3")
    throw new Error("CONNECTED_DEPLOYMENT_UNSUPPORTED")
  if (bootstrap.deployment.venue === "MERKLE_V4" || bootstrap.trade) {
    validateMerkleV4Bootstrap(bootstrap)
    merkleV4ConfigFromBootstrap(bootstrap)
    return
  }
  const reasons = elysiumBootstrapReasons(bootstrap)
  if (reasons.length) throw new Error(reasons.join(", "))
}

export function elysiumBootstrapReasons(
  bootstrap: BootstrapResponse,
): string[] {
  // Full V4 metadata must agree before admitting any NFT/mining operation.
  // A complete swap configuration alone never grants mint permission.
  let validV4 = false
  try {
    const config = merkleV4ConfigFromBootstrap(bootstrap)
    validV4 = Boolean(config && sameIdentity(config.token, ELYSIUM.token) &&
      sameIdentity(config.poolManager, ELYSIUM.poolManager) && sameIdentity(config.poolId, ELYSIUM.poolId) &&
      sameIdentity(config.hook, ELYSIUM.hook) && sameIdentity(bootstrap.contracts.lpLockAddress, ELYSIUM.lpLock) &&
      sameIdentity(bootstrap.deployment.instanceId, ELYSIUM.instanceId) &&
      sameIdentity(bootstrap.contracts.readinessAddress, ELYSIUM.readiness) &&
      sameIdentity(bootstrap.contracts.randomnessAdapterAddress, ELYSIUM.readiness))
  } catch { /* Invalid deployment evidence grants no permissions. */ }
  const token = bootstrap.tokens.find((item) =>
    sameIdentity(item.tokenAddress, ELYSIUM.token),
  )
  return [
    ...(bootstrap.sourceChainId !== ELYSIUM.chainId ||
    bootstrap.deployment.sourceChainId !== ELYSIUM.chainId ||
    bootstrap.deployment.executionChainId !== ELYSIUM.chainId ||
    bootstrap.contracts.mintChainId !== ELYSIUM.chainId
      ? ["ELYSIUM_CHAIN_MISMATCH"]
      : []),
    ...(!sameIdentity(
      bootstrap.contracts.collectionAddress,
      ELYSIUM.collection,
    ) ||
    !sameIdentity(bootstrap.contracts.rewardVaultAddress, ELYSIUM.vault) ||
    !token ||
    token.chainId !== ELYSIUM.chainId ||
    !validV4 ||
    bootstrap.tokens.some(
      (item) =>
        item.chainId !== ELYSIUM.chainId ||
        !sameIdentity(item.tokenAddress, ELYSIUM.token),
    )
      ? ["ELYSIUM_DEPLOYMENT_MISMATCH"]
      : []),
    ...(!["VERIFIED_READ_ONLY", "VERIFIED_WRITE_ENABLED"].includes(
      bootstrap.deployment.projectContractsStatus,
    )
      ? ["ELYSIUM_DEPLOYMENT_PROVISIONAL"]
      : []),
  ]
}

export function isElysium(bootstrap: BootstrapResponse | null): boolean {
  return (
    bootstrap?.sourceChainId === ELYSIUM.chainId ||
    bootstrap?.contracts.mintChainId === ELYSIUM.chainId
  )
}

/** Deployment permission, not eligibility or a signed mint authorization. */
export function elysiumWriteBlock(
  bootstrap: BootstrapResponse | null,
): string | null {
  if (!bootstrap || !isElysium(bootstrap)) return null
  const identity = elysiumBootstrapReasons(bootstrap)[0]
  if (identity) return identity
  if (!bootstrap.capabilities.publicMintEnabled) return "MINT_DISABLED"
  if (
    !bootstrap.capabilities.mainnetWritesEnabled ||
    !bootstrap.deployment.writesEnabled
  )
    return "MAINNET_WRITES_DISABLED"
  if (
    bootstrap.environment !== "production" ||
    bootstrap.forkTestOnly ||
    bootstrap.capabilities.mintGatewayMode !== "MAINNET_READ_ONLY" ||
    bootstrap.capabilities.randomnessRuntimeMode !== "DISABLED" ||
    bootstrap.deployment.projectContractsStatus !== "VERIFIED_WRITE_ENABLED"
  )
    return "EXECUTION_MODE_DISABLED"
  if (
    !sameIdentity(
      bootstrap.contracts.authorizationSignerAddress,
      ELYSIUM.signer,
    ) ||
    !sameIdentity(
      bootstrap.contracts.randomnessAdapterAddress,
      ELYSIUM.readiness,
    )
  )
    return "ELYSIUM_MINT_IDENTITY_MISMATCH"
  if (
    bootstrap.deployment.sourceTokenStatus !== "REGISTERED" ||
    !bootstrap.tokens.some(
      (token) =>
        token.tracking && sameIdentity(token.tokenAddress, ELYSIUM.token),
    )
  )
    return "CONTRACT_NOT_CONFIGURED"
  return null
}

/** BE EAGER fee state already incorporates indexer/on-chain readiness; Quiver approval is unrelated. */
export function elysiumMintStateBlock(
  bootstrap: BootstrapResponse,
  state: BootstrapResponse["mintState"] | null = bootstrap.mintState,
): string | null {
  const deployment = elysiumWriteBlock(bootstrap)
  if (deployment) return deployment
  if (!state?.available || !state.mintEnabled) return "MINT_STATE_UNAVAILABLE"
  if (
    state.source !== "MAINNET_EVM" ||
    state.gatewayMode !== "MAINNET_READ_ONLY" ||
    state.testOnly !== false ||
    state.contractAddress?.toLowerCase() !== ELYSIUM.collection.toLowerCase() ||
    state.rewardPolicyHash !== ELYSIUM.policyHash ||
    state.rewardPolicyVersion !== ELYSIUM.policyVersion ||
    state.randomnessAdapter?.toLowerCase() !==
      ELYSIUM.readiness.toLowerCase() ||
    state.randomnessProviderAvailable !== true ||
    state.randomnessAdapterTestOnly !== false ||
    (state.providerCheckMode !== undefined &&
      state.providerCheckMode !== "EAGER")
  )
    return "ELYSIUM_MINT_IDENTITY_MISMATCH"
  const cache = state.cache
  const now = responseNow(state === bootstrap.mintState ? bootstrap : state)
  if (
    !cache ||
    cache.stale ||
    cache.errorCode !== null ||
    !cache.updatedAt ||
    !cache.expiresAt ||
    now === null ||
    Math.min(
      Date.parse(cache.expiresAt),
      Date.parse(cache.updatedAt) + cache.ttlSeconds * 1000,
    ) <= now
  )
    return "MINT_DISPLAY_CACHE_STALE"
  return null
}
/** Owner redemption has an explicit policy and global writes gate, independent of public mint/Quiver. */
export function elysiumOwnerRedeemEnabled(
  bootstrap: BootstrapResponse | null,
): boolean {
  return Boolean(
    bootstrap &&
    elysiumBootstrapReasons(bootstrap).length === 0 &&
    bootstrap.environment === "production" &&
    !bootstrap.forkTestOnly &&
    bootstrap.deployment.projectContractsStatus === "VERIFIED_WRITE_ENABLED" &&
    bootstrap.deployment.writesEnabled &&
    bootstrap.capabilities.mainnetWritesEnabled &&
    import.meta.env.VITE_ELYSIUM_REDEEM_POLICY === "OWNER_REDEMPTION",
  )
}
export function nativeSymbol(
  chainId: number | null | undefined,
): "HYPE" | "ETH" {
  return chainId === ELYSIUM.chainId ? "HYPE" : "ETH"
}

export function elysiumHealthReasons(
  health: unknown,
  now = Date.now(),
): string[] {
  const value = health as {
    chainId?: number
    netBuy?: {
      mode?: string
      status?: string
      chainId?: number
      tokenAddress?: string
      poolAddress?: string
      poolManager?: string
      poolId?: string
      lastSuccessfulReadAt?: string
    }
  } | null
  const netBuy = value?.netBuy
  const timestamp = Date.parse(netBuy?.lastSuccessfulReadAt ?? "")
  if (
    value?.chainId !== ELYSIUM.chainId ||
    netBuy?.chainId !== ELYSIUM.chainId ||
    netBuy?.mode !== "MERKLE_V4_EVENTS" ||
    !sameIdentity(netBuy?.tokenAddress, ELYSIUM.token) ||
    !sameIdentity(netBuy?.poolManager, ELYSIUM.poolManager) ||
    !sameIdentity(netBuy?.poolId, ELYSIUM.poolId)
  )
    return ["ELYSIUM_API_IDENTITY_MISMATCH"]
  return netBuy?.status !== "READABLE" ||
    !Number.isFinite(timestamp) ||
    now < timestamp ||
    now - timestamp > 30_000
    ? ["ELYSIUM_API_READ_UNAVAILABLE"]
    : []
}
